/**
 * The burned stamp's geometry — **in the output image's own pixels**, mirroring
 * `attachments/services/image_stamp_service.py` integer for integer.
 *
 * This file exists so the preview on the canvas and the pixels in the bucket
 * describe the same document. It has diverged from the server three times, so
 * read the four rules below before touching it.
 *
 * ## 1. The check is `stamp-geometry.fixture.json`, not this comment
 *
 * The API generates that table from its own functions and fails its own test
 * when the table stops matching them; `gen-stamp-geometry-fixture.py` copies it
 * here and `stamp-geometry.test.ts` asserts everything below reproduces it. Do
 * not hand-adjust a constant here and call it fixed — regenerate on the API
 * side, copy, and let the test say whether this agrees.
 *
 * Every number here comes from a function over there (`compass_diameter`,
 * `label_budget`, `_corner_box`, `_corner_margin`, `_output_box`). The one
 * previous attempt that transcribed a formula instead went stale within a day
 * while both sides' tests passed, because the fixture was computing the same
 * stale formula.
 *
 * ## 2. The unit is OUTPUT pixels, not the slot's
 *
 * The server scales every crop **to** a longest side of `_OUTPUT_SIZE` (500) —
 * up as well as down — and burns onto that. So the geometry is a function of
 * the crop's *aspect* and nothing else: the corner inset is always 13px, the
 * glyph always 11px. Multiply by `displayScale` to draw at the slot's size.
 *
 * An earlier version scaled the fractions by the *slot's* longest side. The
 * fractions happen to agree to ~1% (7/300 ≈ 11/500), which is why it looked
 * right — but the floors, the compass bound and the map budget are absolute
 * output pixels, and those diverged badly.
 *
 * ## 3. Two scales, and which one applies is the subtlety
 *
 * A *glyph* badge — compass disc, metadata font, corner inset — comes off the
 * LONGEST side (`_stamp_scale`). Its job is to be legible, not to be a fraction
 * of the photo, so scaling each axis independently would make the same compass
 * tall and thin on a banner and squat on a portrait.
 *
 * A *box* asset — the map thumbnail and the logo badge, which share one box —
 * comes off the SHORTEST side (`_corner_box`). Taking 30% of the longest side
 * gave a portrait output a corner wider than the fraction of its own width the
 * layout had budgeted: on a 250x500 output the map's frame reached past the
 * middle of the image and painted over the bottom-left location stamp, the GPS
 * evidence the overlay exists to burn in. For a square image the two coincide —
 * which is why one longest-side reading looked correct for as long as every
 * stored photo was square.
 *
 * ## 4. Nothing here is a rendering judgement
 *
 * No "floor it so a tiny preview still shows something". That belongs to
 * whoever draws — see `PhotoStampOverlay`. A preview floor caused the second
 * divergence: `max(16, textWidth)` turned the server's "no room, drop the map"
 * into "one character of text, keep the map", the collision the budget exists
 * to prevent. The server's OWN floors (`max(4, …)`, `_MIN_FONT_PX`) are part of
 * the arithmetic and stay.
 *
 * **Known residual:** the server steps the glyph down when a line of it will not
 * fit the budget (`while font_size > _MIN_FONT_PX …`), which needs real font
 * metrics. The preview does not replicate that, so a very narrow slot previews a
 * larger glyph than it burns. Everything else is exact.
 */

/** The stored image's longest side — the server's `_OUTPUT_SIZE`. */
export const OUTPUT_SIZE = 500;
/** `_stamp_scale`'s divisor: the crop viewport the client measures in. */
const STAMP_SCALE_BASE = 300;
/** Glyph badges, off the LONGEST side — `_stamp_scale`. */
const COMPASS_DIAMETER = 26;
const MARGIN = 8;
/**
 * `_LABEL_FONT_BASE_TALL` / `_LABEL_FONT_BASE_SHORT` — 11px and 16px on a 500px
 * output, chosen by how tall the SLOT is (`SLOT_HEIGHT_BREAK`).
 *
 * The stamp is a fixed fraction of the photo and the photo is drawn at the size
 * of the box holding it, so one size cannot serve both: at 18px a half-page
 * image input printed the location text at about half a centimetre, and at 11px
 * a table thumbnail prints it at roughly 4px. A short slot therefore burns the
 * larger glyph, because its photo is reduced on the page and the stamp has to
 * survive the reduction.
 *
 * 11 is also the floor of what the server's arithmetic can express: the next
 * base down meets `MIN_FONT_PX`, where the narrow-box step-down loop it exists
 * for has nothing left to give up.
 */
const FONT_SIZE_TALL = 7;
const FONT_SIZE_SHORT = 10;
/** `_SLOT_HEIGHT_BREAK` — slot height in page px at or below which the larger
 *  glyph is burned. About half an A4 page. */
const SLOT_HEIGHT_BREAK = 400;
/** The smallest glyph the server will burn — `_MIN_FONT_PX`. Lowered to 10 with
 *  the base, to keep the step-down loop one step of headroom below it. */
const MIN_FONT_PX = 10;
/** The corner box, off the SHORTEST side — `_corner_box`. 3:2. */
const CORNER_WIDTH = 0.3;
const CORNER_HEIGHT = 0.2;
/** The white contrast frame drawn around the map, per side — `_MAP_FRAME_PX`. */
const MAP_FRAME_PX = 2;
/** The metadata block's share of the width, before its own insets and the map's
 *  corner come out of it — `_LABEL_WIDTH_FRAC`. */
const TEXT_WIDTH = 0.6;

/**
 * Python's `round`, which is half-to-EVEN, not half-up.
 *
 * `_output_box` rounds the short side, and a 1:8 crop lands on exactly 62.5 —
 * where Python gives 62 and `Math.round` gives 63. One pixel, and it walks
 * through `_corner_box` into the compass bound, so the fixture fails at a shape
 * the server's own tests exercise.
 */
function roundHalfEven(value: number): number {
  const floor = Math.floor(value);
  if (value - floor !== 0.5) return Math.round(value);
  return floor % 2 === 0 ? floor : floor + 1;
}

/** The stored image's dimensions: this shape, longest side `OUTPUT_SIZE`. */
export function outputBox(width: number, height: number): {width: number; height: number} {
  const longest = Math.max(width, height);
  if (longest <= 0) return {width: OUTPUT_SIZE, height: OUTPUT_SIZE};
  const scale = OUTPUT_SIZE / longest;
  return {
    width: Math.max(1, roundHalfEven(width * scale)),
    height: Math.max(1, roundHalfEven(height * scale)),
  };
}

export interface StampGeometry {
  /** Inset from the image's edges, all four corners. */
  margin: number;
  /** The compass disc, square. A value below 1 means no room for one at all. */
  compass: number;
  /** The map thumbnail and the logo badge share a box. */
  badge: {width: number; height: number};
  /** The metadata block's wrapping width. */
  textWidth: number;
  /**
   * Metadata font size — the size the burn STARTS from, not always the one it
   * ends at.
   *
   * `stamp_image` runs a second, height-based step-down after this: when the
   * short-slot branch raises the glyph and the wrapped block then overflows the
   * room the top corners leave it, the server steps back down, floored at the
   * size this returns for `slotHeight: undefined`. So on a short slot this is a
   * ceiling, and the burn lands somewhere in `[tall-branch size, this]`.
   *
   * **Not reproduced here, deliberately.** That step reads the measured height
   * of the real wrapped label, and this preview draws placeholder metadata in
   * the DOM — it does not have the text the capture will carry, so it could not
   * land on the same number even with the measurement. The fixture publishes
   * `labelAvailHeight` for a consumer that does have both; anything drawing a
   * REAL label should step down against it rather than trusting this value.
   */
  fontSize: number;
  /**
   * Whether the map is drawn **at all**.
   *
   * Not the same question as `show_map`. When the label's width budget goes
   * negative there is no room for both, and the map is what gives way: it is a
   * locator rendered FROM the coordinates in the label, so dropping it loses a
   * convenience where the reverse burns a thumbnail over the GPS fix the overlay
   * exists to record. A photo with no label keeps its map at any shape, since
   * there is nothing to collide with.
   */
  drawMap: boolean;
  /** Multiply every value above by this to draw at the size measured. */
  displayScale: number;
}

/**
 * The stamp the server will burn onto a crop of the given shape.
 *
 * `width` / `height` are any measurement of that shape — the slot as rendered,
 * the crop rect, the output itself. Only the ratio is read; the absolute size
 * comes back as `displayScale`.
 *
 * `map` and `label` are what the slot's config asks for. Both reach the geometry
 * because the budget is a three-way interaction: the label's width depends on
 * whether a map is drawn, and whether a map is drawn depends on whether the
 * budget survives a label. The server decides this in one function
 * (`label_budget`) for the same reason.
 */
export function stampGeometry(
  width: number,
  height: number,
  {
    map = false,
    label = false,
    slotHeight,
  }: {map?: boolean; label?: boolean; slotHeight?: number} = {},
): StampGeometry {
  const out = outputBox(width, height);
  const longest = Math.max(out.width, out.height);
  const shortest = Math.min(out.width, out.height);
  const scale = longest / STAMP_SCALE_BASE;

  const margin = Math.max(4, Math.trunc(MARGIN * scale));
  // `_label_pad_x` — the same formula as the margin, and deliberately its own
  // name over there: the two are equal by arithmetic, not by meaning. One is how
  // far an overlay sits from an edge, the other how far text sits from its own
  // box's edge, and the width budget below subtracts both.
  const padX = margin;
  const badge = {
    width: Math.max(1, Math.trunc(shortest * CORNER_WIDTH)),
    height: Math.max(1, Math.trunc(shortest * CORNER_HEIGHT)),
  };
  // Bounded by the room its NEIGHBOURS leave — the map's box off the bottom
  // edge, the logo's off the left — not merely by the short side. Clamping it to
  // the short side let the disc consume the whole of it, so the compass stopped
  // being mutually exclusive with the assets the corner box exists to
  // de-conflict, and the map (pasted last) painted over it.
  const compass = Math.min(
    Math.trunc(COMPASS_DIAMETER * scale),
    out.height - 2 * margin - badge.height - 2 * MAP_FRAME_PX,
    out.width - 2 * margin - badge.width,
  );

  let drawMap = map;
  let textWidth: number;
  if (label) {
    // 60% of the width, and never more than the width MINUS the block's own
    // insets. The 60% alone was a *text* budget checked against the whole image,
    // so a narrow output had every wrapped line clipped at the right edge while
    // the box around it still looked right.
    textWidth = Math.min(Math.trunc(out.width * TEXT_WIDTH), out.width - 2 * margin - 2 * padX);
    if (drawMap) {
      // The two boxes may touch, and that is the whole budget. `-1` because the
      // label's rounded rectangle paints its right coordinate too.
      const reserved = out.width - (badge.width + 2 * MAP_FRAME_PX) - 2 * margin - 2 * padX - 1;
      if (reserved <= 0) drawMap = false;
      else textWidth = Math.min(textWidth, reserved);
    }
    textWidth = Math.max(1, textWidth);
  } else {
    // No label is drawn, so nothing collides and nothing is withdrawn.
    textWidth = Math.max(1, Math.trunc(out.width * TEXT_WIDTH));
  }

  return {
    margin,
    compass,
    badge,
    textWidth,
    // `undefined` takes the TALL branch, matching the server's `None` — the
    // smaller glyph, and what a capture that never says how tall its slot is
    // gets burned. A preview that guessed the larger one would promise a stamp
    // the upload does not ask for.
    fontSize: Math.max(
      MIN_FONT_PX,
      Math.trunc(
        (slotHeight !== undefined && slotHeight <= SLOT_HEIGHT_BREAK
          ? FONT_SIZE_SHORT
          : FONT_SIZE_TALL) * scale,
      ),
    ),
    drawMap,
    displayScale: Math.max(width, height, 1) / longest,
  };
}
